'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import {
  AlertCircle,
  ArrowRight,
  Check,
  ChevronRight,
  Copy,
  Download,
  ExternalLink,
  FileText,
  Lock,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  UserCheck,
} from 'lucide-react';
import { Carousel } from '@mantine/carousel';
import pageStyles from '@/src/app/[lang]/north-star-dashboard/page.module.scss';
import {
  buildConfluenceMarkdown,
  downloadPlanMarkdown,
} from '@/src/lib/measurementPlan/confluenceTemplate';
import type { MeasurementPlan, Metric } from '@/src/lib/measurementPlan/schema';

import '@mantine/carousel/styles.css';

interface CockpitResultProps {
  plan: MeasurementPlan;
  isEn: boolean;
  lang: 'fr' | 'en';
  onTrack: (
    type:
      | 'mvp_plan_confluence_copied'
      | 'mvp_plan_md_downloaded'
      | 'mvp_plan_human_review_requested'
  ) => void;
}

function classNames(...classes: (string | false | undefined)[]) {
  return classes.filter(Boolean).join(' ');
}

interface DisplayMetric {
  label: string;
  description: string;
  why: string;
  formula: string;
  target: string;
  source?: string;
}

function toDisplayMetric(metric: Metric): DisplayMetric {
  return {
    label: metric.name,
    description: metric.reason,
    why: metric.reason,
    formula: metric.formula,
    target: metric.target,
    source: metric.dataSource,
  };
}

function MetricCard({
  metric,
  accent,
  isEn,
  index,
}: {
  metric: DisplayMetric;
  accent: 'blue' | 'teal';
  isEn: boolean;
  index: number;
}) {
  const labels = {
    definition: isEn ? 'Definition' : 'Définition',
    why: isEn ? 'Why it matters' : 'Pourquoi cela compte',
    formula: isEn ? 'Formula' : 'Formule',
    source: isEn ? 'Source' : 'Source',
    target: isEn ? 'Target' : 'Objectif',
  };

  const Icon = accent === 'blue' ? Target : ShieldCheck;

  return (
    <div
      className={classNames(pageStyles.metricCard, accent === 'teal' && pageStyles.metricCardTeal)}
    >
      <Icon size={160} className={pageStyles.metricCardBgIcon} />
      <div className={pageStyles.metricCardHeader}>
        <span className={pageStyles.metricCardNumber}>{String(index + 1).padStart(2, '0')}</span>
        <span className={pageStyles.metricCardTitle}>{metric.label}</span>
        <span className={pageStyles.metricCardTarget}>{metric.target}</span>
      </div>
      <div className={pageStyles.metricCardBody}>
        <div className={pageStyles.metricCardSection}>
          <span className={pageStyles.metricCardLabel}>{labels.definition}</span>
          <p>{metric.description}</p>
        </div>
        <div className={pageStyles.metricCardSection}>
          <span className={pageStyles.metricCardLabel}>{labels.why}</span>
          <p>{metric.why}</p>
        </div>
        <div className={pageStyles.metricCardSection}>
          <span className={pageStyles.metricCardLabel}>
            {metric.source ? labels.source : labels.formula}
          </span>
          <p>{metric.source || metric.formula}</p>
        </div>
      </div>
    </div>
  );
}

function MetricCarousel({
  metrics,
  accent,
  isEn,
  title,
  icon: Icon,
}: {
  metrics: DisplayMetric[];
  accent: 'blue' | 'teal';
  isEn: boolean;
  title: string;
  icon: React.ElementType;
}) {
  return (
    <div className={pageStyles.metricCarousel}>
      <div
        className={classNames(
          pageStyles.metricCarouselHeader,
          accent === 'teal' && pageStyles.metricCarouselHeaderTeal
        )}
      >
        <Icon size={18} />
        <span>{title}</span>
      </div>
      <Carousel
        slideSize={{ base: '100%', sm: '50%', md: '33.333%' }}
        slideGap={{ base: 'md', sm: 'lg' }}
        align="start"
        withControls
        withIndicators
        loop={false}
        classNames={{
          root: pageStyles.carouselRoot,
          viewport: pageStyles.carouselViewport,
          container: pageStyles.carouselContainer,
          slide: pageStyles.carouselSlide,
          control: pageStyles.carouselControl,
          indicator: pageStyles.carouselIndicator,
        }}
      >
        {metrics.map((metric, idx) => (
          <Carousel.Slide key={`${accent}-${idx}`}>
            <MetricCard metric={metric} accent={accent} isEn={isEn} index={idx} />
          </Carousel.Slide>
        ))}
      </Carousel>
    </div>
  );
}

export function CockpitResult({ plan, isEn, lang, onTrack }: CockpitResultProps) {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');
  const [mdStatus, setMdStatus] = useState<'idle' | 'downloaded'>('idle');

  const labels = {
    productTitle: isEn ? 'Product metrics' : 'Métriques produit',
    techTitle: isEn ? 'Quality & risk metrics' : 'Métriques qualité & risque',
    summaryTitle: isEn ? 'First decision rule' : 'Première règle de décision',
    nextTitle: isEn ? 'What happens next' : 'La suite',
    similarTitle: isEn ? 'Keep going' : 'Poursuivre',
    similarSubtitle: isEn
      ? 'Download the full plan or book a call to refine it.'
      : 'Téléchargez le plan complet ou prenez rendez-vous pour l’affiner.',
    downloadMd: isEn ? 'Download .md' : 'Télécharger .md',
    downloaded: isEn ? 'Downloaded!' : 'Téléchargé !',
    copyConfluence: isEn ? 'Copy for Confluence' : 'Copier pour Confluence',
    copied: isEn ? 'Copied!' : 'Copié !',
    bookCta: isEn ? 'Book a 20-min call' : 'Réserver un appel de 20 min',
    issueLabel: isEn ? 'The issue' : 'Le problème',
    hypothesisLabel: isEn ? 'The hypothesis' : 'L’hypothèse',
    valueLabel: isEn ? 'The value' : 'La valeur',
    northStarBadge: isEn ? 'North Star' : 'North Star',
    trustPrivacy: isEn ? 'Privacy-first' : 'Confidentialité',
    trustHuman: isEn ? 'Human expert review' : 'Revue humaine',
    trustNoLock: isEn ? 'No lock-in' : 'Sans engagement',
    docTitle: isEn ? 'Your Confluence document' : 'Votre document Confluence',
    docSubtitle: isEn
      ? 'Ready to paste into Confluence — checklists included.'
      : 'Prêt à coller dans Confluence — checklists incluses.',
    docGenerated: isEn ? 'Generated by Averiane' : 'Généré par Averiane',
    docSummary: isEn ? 'Executive summary' : 'Résumé exécutif',
    docMetrics: isEn ? 'Metrics to operationalise' : 'Métriques à rendre opérationnelles',
    docAcceptance: isEn ? 'Acceptance criteria' : "Critères d'acceptation",
    docRules: isEn ? 'Decision rules' : 'Règles de décision',
    docMissing: isEn ? 'Missing information' : 'Informations manquantes',
    docFullNote: isEn
      ? 'The full version adds per-metric action checklists, the instrumentation plan, the first-30-days checklist and the debt register.'
      : 'La version complète ajoute les checklists d’action par métrique, le plan d’instrumentation, la checklist des 30 premiers jours et le registre des dettes.',
    aiCollectText: isEn
      ? 'Want to automate the collection of these metrics with AI? We will walk you through the approach in a 20-minute call.'
      : 'Vous souhaitez automatiser la collecte de ces métriques par IA ? Nous vous expliquons l’approche en 20 minutes, par téléphone.',
    aiCollectCta: isEn ? 'Talk to an expert' : 'En parler à un expert',
  };

  const productMetrics = useMemo(
    () => plan.metrics.filter((m) => m.category === 'business').map(toDisplayMetric),
    [plan.metrics]
  );

  const techMetrics = useMemo(
    () => plan.metrics.filter((m) => m.category !== 'business').map(toDisplayMetric),
    [plan.metrics]
  );

  const summaryRule = plan.continueCorrectPivotStopRules[0];
  const nextItems = [...plan.acceptanceCriteria.slice(0, 2), ...plan.handoverCriteria.slice(0, 1)];

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(buildConfluenceMarkdown(plan, lang));
      setCopyStatus('copied');
      onTrack('mvp_plan_confluence_copied');
      setTimeout(() => setCopyStatus('idle'), 2500);
    } catch {
      // clipboard may be unavailable
    }
  }

  function handleDownload() {
    downloadPlanMarkdown(plan, lang);
    setMdStatus('downloaded');
    onTrack('mvp_plan_md_downloaded');
    setTimeout(() => setMdStatus('idle'), 2500);
  }

  function handleHumanReview() {
    onTrack('mvp_plan_human_review_requested');
  }

  return (
    <>
      {/* North Star focal */}
      <div className={pageStyles.northStarFocal} data-testid="cockpit-result">
        <div className={pageStyles.northStarFocalGlow} />
        <h2 className={pageStyles.northStarFocalLabel}>{plan.northStarMetric}</h2>
        <div className={pageStyles.northStarFocalBadge}>{labels.northStarBadge}</div>
      </div>

      {/* Foundation hero */}
      <div className={pageStyles.foundationHero}>
        <div className={classNames(pageStyles.foundationCard, pageStyles.foundationCardRed)}>
          <AlertCircle size={140} className={pageStyles.foundationCardBgIcon} />
          <div className={pageStyles.foundationCardHeader}>
            <div className={pageStyles.foundationCardIcon}>
              <AlertCircle size={20} />
            </div>
            <div className={pageStyles.foundationCardLabel}>{labels.issueLabel}</div>
          </div>
          <h3 className={pageStyles.foundationCardTitle}>{plan.businessObjective}</h3>
        </div>

        <div className={classNames(pageStyles.foundationCard, pageStyles.foundationCardBlue)}>
          <Target size={140} className={pageStyles.foundationCardBgIcon} />
          <div className={pageStyles.foundationCardHeader}>
            <div className={pageStyles.foundationCardIcon}>
              <Target size={20} />
            </div>
            <div className={pageStyles.foundationCardLabel}>{labels.hypothesisLabel}</div>
          </div>
          <h3 className={pageStyles.foundationCardTitle}>{plan.executiveSummary}</h3>
        </div>

        <div className={classNames(pageStyles.foundationCard, pageStyles.foundationCardGold)}>
          <TrendingUp size={140} className={pageStyles.foundationCardBgIcon} />
          <div className={pageStyles.foundationCardHeader}>
            <div className={pageStyles.foundationCardIcon}>
              <TrendingUp size={20} />
            </div>
            <div className={pageStyles.foundationCardLabel}>{labels.valueLabel}</div>
          </div>
          <h3 className={pageStyles.foundationCardTitle}>
            {plan.scope[0] || plan.executiveSummary}
          </h3>
        </div>
      </div>

      {/* Section — Metrics */}
      {productMetrics.length > 0 && (
        <MetricCarousel
          metrics={productMetrics}
          accent="blue"
          isEn={isEn}
          title={labels.productTitle}
          icon={Target}
        />
      )}
      {techMetrics.length > 0 && (
        <MetricCarousel
          metrics={techMetrics}
          accent="teal"
          isEn={isEn}
          title={labels.techTitle}
          icon={ShieldCheck}
        />
      )}

      {/* Confluence document preview — makes the deliverable visible before download */}
      <div className={pageStyles.docPreview} data-testid="confluence-preview">
        <div className={pageStyles.docPreviewHeader}>
          <FileText size={20} className={pageStyles.docPreviewIcon} aria-hidden="true" />
          <div className={pageStyles.docPreviewHeadings}>
            <span className={pageStyles.docPreviewTitle}>{labels.docTitle}</span>
            <p className={pageStyles.docPreviewSubtitle}>{labels.docSubtitle}</p>
          </div>
          <button
            type="button"
            className={pageStyles.restart}
            onClick={handleCopy}
            disabled={copyStatus === 'copied'}
          >
            {copyStatus === 'copied' ? <Check size={16} /> : <Copy size={16} />}
            {copyStatus === 'copied' ? labels.copied : labels.copyConfluence}
          </button>
        </div>

        <div className={pageStyles.docPaper}>
          <h3 className={pageStyles.docPaperTitle}>{plan.title}</h3>
          <p className={pageStyles.docPaperMeta}>
            {labels.docGenerated} — {new Date().toISOString().split('T')[0]} ·{' '}
            {plan.northStarMetric}
          </p>

          <h4 className={pageStyles.docPaperHeading}>{labels.docSummary}</h4>
          <p className={pageStyles.docPaperText}>{plan.executiveSummary}</p>

          <h4 className={pageStyles.docPaperHeading}>{labels.docMetrics}</h4>
          <ul className={pageStyles.docChecklist}>
            {plan.metrics.map((metric) => (
              <li key={metric.name}>
                <span className={pageStyles.docCheckBox} aria-hidden="true" />
                <span>
                  <strong>{metric.name}</strong> — {isEn ? 'target' : 'cible'}{' '}
                  <em>{metric.target}</em> · {metric.cadence} · {metric.owner}
                </span>
              </li>
            ))}
          </ul>

          <h4 className={pageStyles.docPaperHeading}>{labels.docAcceptance}</h4>
          <ul className={pageStyles.docChecklist}>
            {plan.acceptanceCriteria.map((item) => (
              <li key={item}>
                <span className={pageStyles.docCheckBox} aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <h4 className={pageStyles.docPaperHeading}>{labels.docRules}</h4>
          <ul className={pageStyles.docRules}>
            {plan.continueCorrectPivotStopRules.map((rule, idx) => (
              <li key={idx}>
                <strong>
                  {isEn ? 'If' : 'Si'} {rule.condition}:
                </strong>{' '}
                {rule.action}
              </li>
            ))}
          </ul>

          {plan.missingInformation.length > 0 && (
            <>
              <h4 className={pageStyles.docPaperHeading}>{labels.docMissing}</h4>
              <ul className={pageStyles.docRules}>
                {plan.missingInformation.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </>
          )}
        </div>
        <p className={pageStyles.docFullNote}>{labels.docFullNote}</p>
      </div>

      {/* AI data-collection mention */}
      <div className={pageStyles.aiCollectBanner}>
        <Sparkles size={18} className={pageStyles.aiCollectIcon} aria-hidden="true" />
        <p className={pageStyles.aiCollectText}>{labels.aiCollectText}</p>
        <Link href={`/${lang}/contact`} className={pageStyles.aiCollectCta}>
          {labels.aiCollectCta}
          <ArrowRight size={14} aria-hidden="true" />
        </Link>
      </div>

      {/* Summary + next steps */}
      <div className={pageStyles.bottomSection}>
        <div className={pageStyles.summaryColumn}>
          <div className={pageStyles.sectionLabel}>{labels.summaryTitle}</div>
          {summaryRule ? (
            <div className={pageStyles.summaryCard}>
              <div className={pageStyles.summaryHeader}>
                <ChevronRight size={18} />
                <span>{summaryRule.condition}</span>
              </div>
              <p>{summaryRule.action}</p>
              <div className={pageStyles.summaryNext}>{plan.missingInformation[0] || ''}</div>
            </div>
          ) : (
            <div className={pageStyles.summaryCard}>
              <div className={classNames(pageStyles.skeletonTitle, pageStyles.skeletonShort)} />
              <div className={pageStyles.skeletonText} />
              <div className={pageStyles.skeletonText} />
            </div>
          )}
        </div>

        <div className={pageStyles.nextStepsColumn}>
          <div className={pageStyles.sectionLabel}>{labels.nextTitle}</div>
          <ol className={pageStyles.nextStepsList}>
            {nextItems.map((item, idx) => (
              <li key={idx}>
                <span className={pageStyles.nextStepsNumber}>{idx + 1}</span>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      {/* Footer CTA banner */}
      <div className={pageStyles.dashboardFooter}>
        <div className={pageStyles.trustBadges}>
          <span className={pageStyles.trustBadge}>
            <Lock size={14} />
            {labels.trustPrivacy}
          </span>
          <span className={pageStyles.trustBadge}>
            <UserCheck size={14} />
            {labels.trustHuman}
          </span>
          <span className={pageStyles.trustBadge}>
            <ExternalLink size={14} />
            {labels.trustNoLock}
          </span>
        </div>

        <div className={pageStyles.footerActions}>
          <button
            type="button"
            className={pageStyles.restart}
            onClick={handleDownload}
            disabled={mdStatus === 'downloaded'}
          >
            {mdStatus === 'downloaded' ? <Check size={16} /> : <Download size={16} />}
            {mdStatus === 'downloaded' ? labels.downloaded : labels.downloadMd}
          </button>
          <button
            type="button"
            className={pageStyles.restart}
            onClick={handleCopy}
            disabled={copyStatus === 'copied'}
          >
            {copyStatus === 'copied' ? <Check size={16} /> : <Copy size={16} />}
            {copyStatus === 'copied' ? labels.copied : labels.copyConfluence}
          </button>
          <Link
            href={`/${lang}/rendez-vous`}
            className={pageStyles.ctaPrimary}
            onClick={handleHumanReview}
          >
            {labels.bookCta}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </>
  );
}
